// Component to handle theme override for landing and auth pages
import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

// Pages that should always use light theme (temporarily)
const LIGHT_THEME_PAGES = [
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/signup",
  "/signup/invitation",
  "/payment/verify",
  "/pricing",
  "/upgrade",
  "/upgrade-plan",
  "/activate-trial",
];

const ThemeOverrideHandler = ({ children }) => {
  const location = useLocation();

  // Function to apply theme override
  const applyThemeOverride = (pathname) => {
    const shouldForceLightTheme = LIGHT_THEME_PAGES.includes(pathname);

    if (shouldForceLightTheme) {
      // Force light theme for these pages - be more aggressive
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
      // Also set a temporary flag to prevent ThemeContext from overriding
      document.documentElement.setAttribute("data-theme-forced", "true");
    } else {
      // Remove the forced flag
      document.documentElement.removeAttribute("data-theme-forced");
      // Restore the user's actual theme preference for other pages
      const savedTheme = localStorage.getItem("theme") || "light";
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(savedTheme);
    }
  };

  // Use layoutEffect to run before paint (earlier than useEffect)
  useLayoutEffect(() => {
    applyThemeOverride(location.pathname);
  }, [location.pathname]);

  // Also use regular useEffect as backup
  useEffect(() => {
    applyThemeOverride(location.pathname);

    // Set up a mutation observer to watch for theme changes and override them
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const shouldForceLightTheme = LIGHT_THEME_PAGES.includes(
            location.pathname
          );
          if (
            shouldForceLightTheme &&
            document.documentElement.classList.contains("dark")
          ) {
            // Someone tried to add dark theme, override it
            document.documentElement.classList.remove("dark");
            document.documentElement.classList.add("light");
          }
        }
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Cleanup observer
    return () => observer.disconnect();
  }, [location.pathname]);

  return children;
};

export default ThemeOverrideHandler;
